import { Building2, ChevronRight, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataCard, DetailRow } from "@/components/ui/data-card";
import { ConfirmDeleteButton } from "@/components/ui/confirm-delete-button";
import { formatAddress } from "@/components/forms/AddressFields";
import type { Customer } from "@/types/tms";

interface CustomerCardProps {
  customer: Customer;
  isChecked: boolean;
  onToggle: (id: string) => void;
  /** Opens the customer detail page. */
  onOpen: (id: string) => void;
  onEdit: (customer: Customer) => void;
  onDelete: (id: string) => void;
  templateCount?: number;
}

/** Mobile/tablet representation of a customer row on the Customers page. */
export function CustomerCard({
  customer,
  isChecked,
  onToggle,
  onOpen,
  onEdit,
  onDelete,
  templateCount = 0,
}: CustomerCardProps) {
  const billingReqs = [
    customer.requires_tender_id && "Tender ID",
    customer.requires_bol && "BOL",
    customer.requires_tender && "Tender",
  ].filter(Boolean) as string[];

  return (
    <DataCard onClick={() => onOpen(customer.id)}>
      <DataCard.Header>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isChecked}
            onCheckedChange={() => onToggle(customer.id)}
            aria-label={`Select ${customer.organization_name}`}
          />
        </span>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium truncate">{customer.organization_name}</span>
        <code className="bg-muted px-2 py-0.5 rounded text-xs">{customer.short_code}</code>
        {templateCount > 0 ? (
          <span className="px-1.5 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
            {templateCount} {templateCount === 1 ? "order template" : "order templates"}
          </span>
        ) : null}
        <span className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(customer)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <ConfirmDeleteButton
            onConfirm={() => onDelete(customer.id)}
            entityName="customer"
            entityLabel={customer.organization_name}
          />
        </span>
      </DataCard.Header>

      <div className="divide-y divide-border">
        <DetailRow
          label="Billing requirements"
          value={
            billingReqs.length > 0 ? (
              <span className="flex flex-wrap justify-end gap-1">
                {billingReqs.map((req) => (
                  <span key={req} className="status-badge status-booked">
                    {req}
                  </span>
                ))}
              </span>
            ) : null
          }
        />
        <DetailRow
          label="NVO authorized"
          value={
            customer.authorized_nvo ? <span className="status-badge status-booked">NVO</span> : null
          }
        />
        <DetailRow
          label="Email"
          value={
            customer.email ? (
              <a
                href={`mailto:${customer.email}`}
                className="text-primary hover:underline truncate"
                onClick={(e) => e.stopPropagation()}
              >
                {customer.email}
              </a>
            ) : null
          }
        />
        <DetailRow label="Address" value={formatAddress(customer) || null} />
      </div>
    </DataCard>
  );
}